/**
 * Product Detail Page
 *
 * Complete detail view for a single product record with tabs
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useProduct, useDeleteProduct } from '../../services/queries/productQueries';
import { usePricesByProduct } from '../../services/queries/priceQueries';
import { useInvoicesByProduct } from '../../services/queries/invoiceQueries';
import { DetailPageLayout } from '../../components/detail/DetailPageLayout';
import { DetailHeader } from '../../components/detail/DetailHeader';
import { DetailTabs } from '../../components/detail/DetailTabs';
import { LayoutSection } from '../../components/detail/LayoutSection';
import { ActivityTab } from '../../components/detail/ActivityTab';
import { usePageLayout } from '../../hooks/useObjectManager';
import { useToast } from '../../contexts/ToastContext';
import { Package, DollarSign, FileText, History, Receipt } from 'lucide-react';
import type { ProductCatalog } from '../../types/index';

interface ProductDetailPageProps {
  id: string;
  onBack: () => void;
}

export const ProductDetailPage: React.FC<ProductDetailPageProps> = ({ id, onBack }) => {
  const { success } = useToast();
  const [activeTab, setActiveTab] = useState('overview');

  const { data: product, isLoading } = useProduct(id);
  const deleteProduct = useDeleteProduct();
  const { data: prices = [] } = usePricesByProduct(id);
  const { data: transactions = [], isLoading: transactionsLoading } = useInvoicesByProduct(id);

  const { data: pageLayout, isLoading: loadingLayout } = usePageLayout('Product__c', product);

  const invoiceStatusColors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    paid: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    overdue: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  };

  const handleDelete = () => {
    if (!product) return;
    if (!window.confirm(`Are you sure you want to delete ${product.name}?`)) return;

    deleteProduct.mutate(id, {
      onSuccess: () => {
        success('Product deleted', `${product.name} has been deleted`);
        onBack();
      },
    });
  };

  if (isLoading) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Products">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DetailPageLayout>
    );
  }

  if (!product) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Products">
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Product not found</p>
        </div>
      </DetailPageLayout>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Package },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'transactions', label: 'Transactions', icon: Receipt },
    { id: 'related', label: 'Related Invoices', icon: FileText },
    { id: 'history', label: 'History', icon: History }
  ];

  return (
    <DetailPageLayout onBack={onBack} backLabel="Back to Products">
      <div className="max-w-6xl mx-auto space-y-6">
        <DetailHeader
          title={product.name}
          subtitle={`${product.productCode} • ${product.category}`}
          recordId={product.id}
          status={product.isActive ? 'Active' : 'Inactive'}
          statusColor={product.isActive 
            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
          }
          onEdit={() => {}}
          onDelete={handleDelete}
        />

        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Unit Price</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {new Intl.NumberFormat('en-US', { style: 'currency', currency: product.currency }).format(product.unitPrice)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Category</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white capitalize">
                {product.category}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Billing Frequency</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white capitalize">
                {product.billingFrequency || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <DetailTabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
          />

          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {loadingLayout ? (
                  <div className="space-y-6">
                    {[1, 2].map(i => (
                      <div key={i} className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                        <div className="animate-pulse space-y-3">
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  pageLayout?.sections.map(section => (
                    <LayoutSection
                      key={section.id}
                      section={section}
                      objectApiName="Product"
                      record={product}
                    />
                  ))
                )}
              </div>
            )}

            {activeTab === 'pricing' && (
              <div className="space-y-6">
                <div className="bg-gray-50 dark:bg-[#334155] rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Pricing Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Standard Price</p>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: product.currency }).format(product.unitPrice)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Currency</p>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">{product.currency}</p>
                    </div>
                  </div>
                </div>
                {prices.length > 0 && (
                  <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                      Prices (pricebook entries)
                    </h3>
                    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                      {prices.map((price) => (
                        <li key={price.id} className="px-6 py-4 flex flex-wrap items-center gap-x-4 gap-y-1">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {price.currency}
                          </span>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {price.billingScheme === 'tiered'
                              ? `Tiered (${price.tiersMode === 'volume' ? 'volume' : 'graduated'})`
                              : 'Per unit'}
                          </span>
                          <span className="text-sm text-gray-900 dark:text-white">
                            {price.billingScheme === 'tiered'
                              ? (price.tiers && price.tiers.length > 0
                                  ? price.tiers.map((t) => {
                                      const upTo = t.upTo === 'inf' ? '∞' : String(t.upTo);
                                      const amt =
                                        t.unitAmount != null
                                          ? new Intl.NumberFormat('en-US', { style: 'currency', currency: price.currency }).format(t.unitAmount / 100)
                                          : t.flatAmount != null
                                            ? new Intl.NumberFormat('en-US', { style: 'currency', currency: price.currency }).format(t.flatAmount / 100)
                                            : '—';
                                      return `Up to ${upTo}: ${amt}`;
                                    }).join(' · ')
                                  : 'Tiered')
                              : new Intl.NumberFormat('en-US', { style: 'currency', currency: price.currency }).format(price.unitAmount / 100)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {prices.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <p>Pricebook entries and client overrides will be displayed here</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'transactions' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Transactions</h3>
                {transactionsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                    <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">No transactions</p>
                    <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                      Invoices that include this product will appear here.
                    </p>
                  </div>
                ) : (
                  <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-[#334155] border-b border-gray-200 dark:border-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Invoice Number</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Client</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Issue Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Due Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {transactions.map((inv) => (
                          <tr key={inv.id} className="hover:bg-gray-50 dark:hover:bg-[#334155]">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Link
                                to={`/invoices/${inv.id}`}
                                className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                {inv.invoiceNumber}
                              </Link>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{inv.clientName}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  invoiceStatusColors[inv.status] ?? invoiceStatusColors.draft
                                }`}
                              >
                                {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {new Intl.NumberFormat('en-US', { style: 'currency', currency: inv.currency }).format(inv.totalAmount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Date(inv.issueDate).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Date(inv.dueDate).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'related' && (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Related invoices will be displayed here</p>
              </div>
            )}

            {activeTab === 'history' && (
              <ActivityTab
                recordId={product.id}
                recordType={undefined}
                onLogActivity={() => {}}
              />
            )}
          </div>
        </div>
      </div>
    </DetailPageLayout>
  );
};
